import React, { useState } from 'react'
import { useMutation } from '@tanstack/react-query'

import { api } from '../lib/api'
import { Button, ErrorMessage } from './ui'
import { providerLabel } from '../lib/format'

// Connecting a fantasy account, in the one place both screens that offer it can
// read from.
//
// It lives here rather than in either page because the two entry points had
// drifted: /account grew the ESPN form while /leagues still offered Sleeper
// alone, so a new user's first screen could only link half the providers the app
// supports. One definition means the next provider appears on both at once.
//
// The iOS twin is LinkAccount in mobile/src/app/leagues.tsx — keep them in step.

// A real espn_s2 runs to several hundred URL-encoded characters. A truncated
// paste is by far the likeliest way linking fails, and it surfaces from ESPN as
// a 401 that reads like expired cookies — so the length is shown back while
// typing rather than explained after the fact.
const ESPN_S2_TYPICAL_LENGTH = 250

function espnS2Hint(value) {
  const length = value.trim().length

  if (length === 0) return `Usually ${ESPN_S2_TYPICAL_LENGTH}+ characters.`
  if (length < ESPN_S2_TYPICAL_LENGTH) {
    return `${length} characters — that looks short. A full espn_s2 is usually ${ESPN_S2_TYPICAL_LENGTH}+, so check the whole value was copied.`
  }

  return `${length} characters — that looks right.`
}

export default function LinkAccountForm({ label, onLinked }) {
  const [provider, setProvider] = useState('sleeper')
  const [username, setUsername] = useState('')
  const [espnS2, setEspnS2] = useState('')
  const [swid, setSwid] = useState('')

  const link = useMutation({
    mutationFn: () =>
      provider === 'espn'
        ? api.linkEspn({ espn_s2: espnS2.trim(), swid: swid.trim() })
        : api.linkSleeper(username),
    onSuccess: () => {
      setUsername('')
      setEspnS2('')
      setSwid('')
      onLinked?.()
    },
  })

  return (
    <form
      className="mt-3 space-y-2"
      onSubmit={(event) => {
        event.preventDefault()
        link.mutate()
      }}
    >
      <label className="block text-xs text-slate-400" htmlFor="link-provider">
        {label}
      </label>

      {/* Radios rather than a select: there are two, and what each one asks for
          differs enough that seeing both labelled is worth the space. */}
      <div className="flex gap-2" id="link-provider" role="radiogroup" aria-label="Provider">
        {['sleeper', 'espn'].map((option) => (
          <button
            key={option}
            type="button"
            role="radio"
            aria-checked={provider === option}
            onClick={() => setProvider(option)}
            className={`rounded-md border px-3 py-1.5 text-sm ${
              provider === option
                ? 'border-slate-400 bg-slate-800 text-slate-100'
                : 'border-slate-700 text-slate-400 hover:text-slate-200'
            }`}
          >
            {providerLabel(option)}
          </button>
        ))}
      </div>

      {provider === 'sleeper' ? (
        <>
          <div className="flex gap-2">
            <input
              id="sleeper-username"
              className="flex-1 rounded-md border border-slate-600 bg-slate-900 px-3 py-2 text-sm"
              placeholder="Sleeper username"
              value={username}
              required
              onChange={(e) => setUsername(e.target.value)}
            />
            <Button type="submit" disabled={link.isPending || !username}>
              {link.isPending ? 'Looking up…' : 'Link'}
            </Button>
          </div>

          {/* Worth saying, because it is not obvious: Sleeper is resolved to a
              stable user id, so renaming yourself there and re-entering the new
              name updates the connection you already have rather than adding a
              second one. */}
          <p className="text-xs text-slate-500">
            Sleeper is read-only and needs no password. Re-entering a username you have already
            linked just refreshes it.
          </p>
        </>
      ) : (
        <>
          {/* ESPN offers no OAuth and no API key, so there is nothing to offer
              but the user's own cookies. Saying why, rather than just asking for
              them, is the difference between a reasonable request and one that
              looks like phishing. */}
          <p className="text-xs text-slate-400">
            ESPN has no way to grant an app access to a private league — no sign-in to approve, no
            API key. Reading your own league needs two cookies from your logged-in ESPN session.
            They are stored encrypted, used only to read, and you can revoke them by logging out of
            ESPN. See the{' '}
            <a className="underline" href="/privacy" target="_blank" rel="noreferrer">
              privacy policy
            </a>
            .
          </p>

          <ol className="list-decimal space-y-1 pl-5 text-xs text-slate-500">
            <li>
              Sign in at{' '}
              <a
                className="underline"
                href="https://fantasy.espn.com"
                target="_blank"
                rel="noreferrer"
              >
                fantasy.espn.com
              </a>{' '}
              and open your league.
            </li>
            <li>Open developer tools (F12), then Application — or Storage in Firefox.</li>
            <li>
              Under Cookies → https://fantasy.espn.com, copy the full values of{' '}
              <code className="text-slate-300">espn_s2</code> and{' '}
              <code className="text-slate-300">SWID</code>.
            </li>
          </ol>

          <div className="space-y-2">
            <div>
              <label className="block text-xs text-slate-400" htmlFor="espn-s2">
                espn_s2
              </label>
              <textarea
                id="espn-s2"
                rows={3}
                className="mt-1 w-full break-all rounded-md border border-slate-600 bg-slate-900 px-3 py-2 font-mono text-xs"
                placeholder="AEB..."
                value={espnS2}
                required
                onChange={(e) => setEspnS2(e.target.value)}
              />
              {/* The length hint is the whole point: a half-copied value is the
                  likeliest failure, and ESPN reports it as a refused cookie
                  rather than as a short one. */}
              <p className="mt-1 text-xs text-slate-500">{espnS2Hint(espnS2)}</p>
            </div>

            <div>
              <label className="block text-xs text-slate-400" htmlFor="espn-swid">
                SWID
              </label>
              <input
                id="espn-swid"
                className="mt-1 w-full rounded-md border border-slate-600 bg-slate-900 px-3 py-2 font-mono text-xs"
                placeholder="{XXXXXXXX-XXXX-XXXX-XXXX-XXXXXXXXXXXX}"
                value={swid}
                required
                onChange={(e) => setSwid(e.target.value)}
              />
              <p className="mt-1 text-xs text-slate-500">
                The braces are fine either way — they are added if missing.
              </p>
            </div>

            <Button type="submit" disabled={link.isPending || !espnS2.trim() || !swid.trim()}>
              {link.isPending ? 'Checking with ESPN…' : 'Link ESPN account'}
            </Button>
          </div>
        </>
      )}

      <ErrorMessage error={link.error} />
    </form>
  )
}
